import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Pagination,
  LinearProgress,
  Tooltip,
  IconButton,
  TableContainer,
} from "@mui/material";
import axios from "axios";
import { FILE_RULES, validateFile } from "../../utils/fileValidation";
import { BASE_URL } from "../../constants";

// Icons (add to your icon imports):
import MedicationIcon from "@mui/icons-material/Medication";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

export default function MedicineMaster() {
  const [form, setForm] = useState({ name: "", brand: "" });
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Excel upload
  const fileInputRef = useRef(null);
  const [excelFile, setExcelFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const handleChange = (field, value) =>
    setForm((p) => ({ ...p, [field]: value }));
  const resetForm = () => setForm({ name: "", brand: "" });

  const fetchMedicines = async (pageNumber = 1) => {
    try {
      setTableLoading(true);
      const res = await axios.get(
        `${BASE_URL}api/user/common/medicine-list?page=${pageNumber}`,
        {},
      );
      setMedicines(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setPage(res.data.page || 1);
    } catch (err) {
      console.error("Fetch medicines error", err);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchMedicines(1);
  }, []);
  const handlePageChange = (_, v) => fetchMedicines(v);

  const handleSubmit = async () => {
    if (!form.name || !form.brand) {
      setError("Medicine name and brand are required");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      const res = await axios.post(
        `${BASE_URL}api/user/common/create-medicine`,
        form,
        {},
      );
      setMessage(res.data.message);
      resetForm();
      fetchMedicines(1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save medicine");
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      setUploadError("Please select an Excel file first");
      return;
    }

    try {
      setUploadLoading(true);
      setUploadError(null);
      setUploadMessage(null);

      const formData = new FormData();
      formData.append("file", excelFile);

      const { data } = await axios.post(
        `${BASE_URL}api/user/common/upload-medicines`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!data.success) {
        setUploadError(data.message || "Upload failed");
        return;
      }

      let msg = data.message;

      if (data.skipped > 0) {
        msg += ` (${data.inserted} inserted, ${data.skipped} skipped)`;
      }

      setUploadMessage(msg);

      console.log(data.skippedRows);

      setExcelFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      fetchMedicines(1);
    } catch (err) {
      setUploadError(
        err.response?.data?.message || err.message || "Upload failed",
      );
    } finally {
      setUploadLoading(false);
    }
  };

  /* ── UI ── */
  return (
    <Box
      sx={{
        background: "#f4f6fb",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "stretch",
          mb: 2,
        }}
      >
        {/* Add Medicine */}
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 450px",
            minWidth: {
              xs: "100%",
              md: 0,
            },
            borderRadius: "16px",
            border: "1px solid #e5e9ef",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(10,31,68,0.06)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderBottom: "1px solid #e5e9ef",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: "linear-gradient(90deg,#2a54a6,#b93acf)",
              },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                background: "linear-gradient(135deg,#2a54a6,#b93acf)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AddIcon sx={{ color: "#fff", fontSize: "0.95rem" }} />
            </Box>
            <Typography
              sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#0a1f44" }}
            >
              Add Single Medicine
            </Typography>
          </Box>

          <Box sx={{ p: 2.5, background: "#f8fafc" }}>
            {message && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  background: "rgba(16,185,129,0.08)",
                  border: "1px solid rgba(16,185,129,0.25)",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <CheckCircleIcon
                  sx={{ fontSize: "0.9rem", color: "#059669" }}
                />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#059669",
                  }}
                >
                  {message}
                </Typography>
              </Box>
            )}
            {error && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  background: "rgba(239,68,68,0.07)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <WarningAmberIcon
                  sx={{ fontSize: "0.9rem", color: "#dc2626" }}
                />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#dc2626",
                  }}
                >
                  {error}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-end",
                flexWrap: "wrap",
                marginBottom: "31px",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography sx={mmLabelStyle}>
                  Medicine Name <Req />
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Paracetamol"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  sx={mmFieldStyle}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography sx={mmLabelStyle}>
                  Brand <Req />
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Crocin"
                  value={form.brand}
                  onChange={(e) => handleChange("brand", e.target.value)}
                  sx={mmFieldStyle}
                />
              </Box>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                startIcon={
                  loading ? null : (
                    <SaveIcon sx={{ fontSize: "0.9rem !important" }} />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 2.5,
                  py: "8.5px",
                  mb: 0.1,
                  background: loading
                    ? "#e5e9ef"
                    : "linear-gradient(135deg,#2a54a6,#b93acf)",
                  color: loading ? "#94a3b8" : "#ffffff",
                  boxShadow: loading
                    ? "none"
                    : "0 4px 14px rgba(185,58,207,0.28)",
                  "&:hover": {
                    background: "linear-gradient(135deg,#1e3d8a,#a02db8)",
                    boxShadow: "0 6px 18px rgba(185,58,207,0.4)",
                  },
                  "&:disabled": { background: "#e5e9ef" },
                  transition: "all 0.25s ease",
                }}
              >
                {loading ? "Saving…" : "Save"}
              </Button>
            </Box>

            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                background: "rgba(42,84,166,0.05)",
                border: "1px solid rgba(42,84,166,0.12)",
                borderRadius: "8px",
                px: 1.25,
                py: 0.9,
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: "0.78rem",
                  color: "#2a54a6",
                  mt: "1px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: "#64748b",
                  lineHeight: 1.6,
                }}
              >
                Enter a <strong>unique medicine name</strong> and its{" "}
                <strong>brand</strong>, then click <strong>Save</strong>. For
                multiple medicines, use the <strong>Bulk Upload</strong>{" "}
                section.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Excel Upload */}
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 450px",
            minWidth: {
              xs: "100%",
              md: 0,
            },
            borderRadius: "16px",
            border: "1px solid #e5e9ef",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(10,31,68,0.06)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderBottom: "1px solid #e5e9ef",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: "linear-gradient(90deg,#0f6e56,#1d9e75)",
              },
            }}
          >
            {/* Left */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  background: "linear-gradient(135deg,#0f6e56,#1d9e75)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <UploadFileIcon sx={{ color: "#fff", fontSize: "0.95rem" }} />
              </Box>

              <Typography
                sx={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: "#0a1f44",
                }}
              >
                Bulk Upload via Excel
              </Typography>

              <Tooltip
                title="Excel must have columns: name, brand"
                arrow
                placement="right"
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    cursor: "help",
                  }}
                />
              </Tooltip>
            </Box>

            {/* Right */}
            <Button
              component="a"
              href="/templates/Medicine_Template.xlsx"
              download
              startIcon={<DownloadIcon />}
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontSize: "0.78rem",
                fontWeight: 700,
                px: 2,
                color: "#2a54a6",
                background: "rgba(42,84,166,0.06)",
                border: "1px solid rgba(42,84,166,0.15)",
                boxShadow: "0 2px 8px rgba(42,84,166,0.08)",
                transition: "all .2s ease",
                whiteSpace: "nowrap",
                "&:hover": {
                  background: "rgba(42,84,166,0.12)",
                  borderColor: "#2a54a6",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(42,84,166,0.18)",
                },
              }}
            >
              Template
            </Button>
          </Box>

          <Box sx={{ p: 2.5, background: "#f8fafc" }}>
            {uploadMessage && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  background: "rgba(16,185,129,0.08)",
                  border: "1px solid rgba(16,185,129,0.25)",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <CheckCircleIcon
                  sx={{ fontSize: "0.9rem", color: "#059669" }}
                />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#059669",
                  }}
                >
                  {uploadMessage}
                </Typography>
              </Box>
            )}
            {uploadError && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  background: "rgba(239,68,68,0.07)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <WarningAmberIcon
                  sx={{ fontSize: "0.9rem", color: "#dc2626" }}
                />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#dc2626",
                  }}
                >
                  {uploadError}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              {/* Drop zone */}
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  flex: 1,
                  minWidth: 220,
                  border: "2px dashed",
                  borderColor: excelFile
                    ? "rgba(16,185,129,0.4)"
                    : "rgba(42,84,166,0.25)",
                  borderRadius: "12px",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  background: excelFile ? "rgba(16,185,129,0.04)" : "#ffffff",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: excelFile ? "rgba(16,185,129,0.6)" : "#2a54a6",
                    background: excelFile
                      ? "rgba(16,185,129,0.07)"
                      : "rgba(42,84,166,0.03)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "9px",
                    background: excelFile
                      ? "rgba(16,185,129,0.1)"
                      : "rgba(42,84,166,0.08)",
                    border: `1px solid ${excelFile ? "rgba(16,185,129,0.25)" : "rgba(42,84,166,0.18)"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {excelFile ? (
                    <CheckCircleIcon
                      sx={{ fontSize: "1.1rem", color: "#059669" }}
                    />
                  ) : (
                    <UploadFileIcon
                      sx={{ fontSize: "1.1rem", color: "#2a54a6" }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "#0a1f44",
                    }}
                  >
                    {excelFile ? excelFile.name : "Click to choose Excel file"}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.68rem", color: "#94a3b8", mt: "1px" }}
                  >
                    {excelFile
                      ? `${(excelFile.size / 1024).toFixed(1)} KB`
                      : ".xlsx or .xls — columns: name, brand"}
                  </Typography>
                </Box>
                {excelFile && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExcelFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "6px",
                      background: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.2)",
                      color: "#dc2626",
                      flexShrink: 0,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "0.75rem" }} />
                  </IconButton>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    const result = await validateFile(
                      f,
                      FILE_RULES.spreadsheet,
                    );
                    if (!result.valid) {
                      setUploadError(result.error);
                      e.target.value = "";
                      return;
                    }
                    setUploadError(null);
                    setExcelFile(f);
                  }}
                />
              </Box>

              <Button
                onClick={handleExcelUpload}
                disabled={uploadLoading || !excelFile}
                startIcon={
                  uploadLoading ? null : (
                    <UploadFileIcon sx={{ fontSize: "0.9rem !important" }} />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 2.5,
                  py: "8.5px",
                  background:
                    !excelFile || uploadLoading
                      ? "#e5e9ef"
                      : "linear-gradient(135deg,#0f6e56,#1d9e75)",
                  color: !excelFile || uploadLoading ? "#94a3b8" : "#ffffff",
                  boxShadow:
                    !excelFile || uploadLoading
                      ? "none"
                      : "0 4px 14px rgba(16,185,129,0.28)",
                  "&:hover": {
                    background: "linear-gradient(135deg,#064e3b,#0f6e56)",
                    boxShadow: "0 6px 18px rgba(16,185,129,0.4)",
                  },
                  "&:disabled": { background: "#e5e9ef" },
                  transition: "all 0.25s ease",
                  flexShrink: 0,
                }}
              >
                {uploadLoading ? "Uploading…" : "Upload"}
              </Button>
            </Box>

            {uploadLoading && (
              <LinearProgress
                sx={{
                  mt: 1.5,
                  borderRadius: "4px",
                  height: "3px",
                  background: "rgba(16,185,129,0.1)",
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg,#0f6e56,#1d9e75)",
                  },
                }}
              />
            )}

            {/* Format hint */}
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                background: "rgba(42,84,166,0.05)",
                border: "1px solid rgba(42,84,166,0.12)",
                borderRadius: "8px",
                px: 1.25,
                py: 0.9,
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: "0.78rem",
                  color: "#2a54a6",
                  mt: "1px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontSize: "0.68rem", color: "#64748b", lineHeight: 1.6 }}
              >
                Your Excel file must have a header row with columns:{" "}
                <strong>name</strong> and <strong>brand</strong>. Each
                subsequent row is one medicine entry.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── Medicines table ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #e5e9ef",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(10,31,68,0.06)",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid #e5e9ef",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              background: "linear-gradient(135deg,#2a54a6,#b93acf)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FormatListBulletedIcon
              sx={{ color: "#fff", fontSize: "0.95rem" }}
            />
          </Box>
          <Typography
            sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#0a1f44" }}
          >
            Saved Medicines
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: "55vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {["#", "Medicine Name", "Brand", "Added On"].map((h, i) => (
                  <TableCell
                    key={h}
                    align={i === 0 ? "center" : "left"}
                    sx={{
                      background: "linear-gradient(135deg,#0a1f44,#1a3568)",
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.82)",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      py: "10px",
                      px: "14px",
                      borderBottom: "none",
                      width: i === 0 ? 48 : "auto",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 4, borderBottom: 0 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={22} sx={{ color: "#b93acf" }} />
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "#94a3b8" }}
                      >
                        Loading medicines…
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : medicines.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 5, borderBottom: 0 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg,rgba(42,84,166,0.08),rgba(185,58,207,0.06))",
                          border: "1px solid rgba(185,58,207,0.15)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <MedicationIcon
                          sx={{ fontSize: "1.2rem", color: "#94a3b8" }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: "#0a1f44",
                        }}
                      >
                        No medicines yet
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "#94a3b8" }}
                      >
                        Add a medicine above or upload an Excel sheet
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                medicines.map((med, idx) => (
                  <TableRow
                    key={med._id}
                    sx={{
                      background: idx % 2 === 0 ? "#ffffff" : "#fafbfc",
                      "&:hover": { background: "rgba(42,84,166,0.03)" },
                      "& .MuiTableCell-root": {
                        py: "8px",
                        px: "14px",
                        fontSize: "0.82rem",
                        color: "#374151",
                        borderBottom: "1px solid #f1f5f9",
                      },
                      "&:last-child .MuiTableCell-root": { borderBottom: 0 },
                    }}
                  >
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          color: "#94a3b8",
                        }}
                      >
                        {(page - 1) * 10 + idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg,#2a54a6,#b93acf)",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.83rem",
                            fontWeight: 600,
                            color: "#0a1f44",
                          }}
                        >
                          {med.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          background: "rgba(42,84,166,0.07)",
                          border: "1px solid rgba(42,84,166,0.15)",
                          borderRadius: "100px",
                          px: "8px",
                          py: "2px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            color: "#2a54a6",
                          }}
                        >
                          {med.brand}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "#64748b" }}
                      >
                        {new Date(med.createdAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "2-digit",
                        })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1.5,
            borderTop: "1px solid #f1f5f9",
            background:
              "linear-gradient(135deg,rgba(42,84,166,0.02),rgba(185,58,207,0.01))",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: "0.75rem",
                borderRadius: "8px",
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                background: "linear-gradient(135deg,#2a54a6,#b93acf)",
                color: "#fff",
                border: "none",
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}

/* ── Helpers ── */
const mmLabelStyle = {
  fontSize: "0.65rem",
  fontWeight: 600,
  color: "#0a1f44",
  textTransform: "uppercase",
  letterSpacing: ".3px",
  mb: 0.55,
};

const mmFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "#ffffff",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#e5e9ef" },
    "&:hover fieldset": { borderColor: "#2a54a6" },
    "&.Mui-focused fieldset": { borderColor: "#b93acf", borderWidth: "1.5px" },
  },
};

const Req = () => (
  <Box component="span" sx={{ color: "#b93acf", ml: "2px", fontWeight: 700 }}>
    *
  </Box>
);
